import { Control, FieldErrors, useFieldArray } from "react-hook-form";
import { TCreateJSA } from "../types";
import { Button, Icon, Input } from "~/components/ui";

interface HazardsStepProps {
  control: Control<TCreateJSA>;
  errors: FieldErrors<TCreateJSA>;
}

const HazardsStep = ({ control }: HazardsStepProps) => {
  const {
    fields: steps,
    append: appendStep,
    remove: removeStep,
  } = useFieldArray({
    control,
    name: "steps",
  });

  return (
    <div className="space-y-6 rounded-xl border border-neutral-200 bg-white p-8">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-Medium text-primary-500">
            Hazards & Controls
          </h2>
          <p className="text-sm text-neutral-500">
            Break the job down into steps, identify hazards, and apply controls.
          </p>
        </div>
        <Button
          type="button"
          onClick={() =>
            appendStep({
              stepNumber: steps.length + 1,
              stepDescription: "",
              hazards: [],
            })
          }
          className="flex items-center gap-2"
        >
          <Icon name="Plus" className="h-4 w-4" />
          Add Job Step
        </Button>
      </div>

      <div className="space-y-6">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className="border border-neutral-200 rounded-lg p-4 bg-neutral-50 relative"
          >
            <button
              type="button"
              onClick={() => removeStep(index)}
              className="absolute top-4 right-4 text-neutral-400 hover:text-red-500 transition-colors"
            >
              <Icon name="Trash2" className="h-4 w-4" />
            </button>
            <h3 className="font-medium text-sm mb-4">Step {index + 1}</h3>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Step Description</label>
                <Input
                  {...control.register(
                    `steps.${index}.stepDescription` as const,
                  )}
                  placeholder="e.g. Isolate electrical power"
                />
              </div>

              {/* Hazards for this step */}
              <div className="pl-4 border-l-2 border-primary-100 mt-4 space-y-4">
                <h4 className="text-sm font-medium text-primary-500">
                  Identified Hazards
                </h4>
                <HazardsList control={control} stepIndex={index} />
              </div>
            </div>
          </div>
        ))}

        {steps.length === 0 && (
          <div className="text-center py-10 border-2 border-dashed border-neutral-200 rounded-lg">
            <p className="text-neutral-500 mb-4">No job steps added yet.</p>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                appendStep({ stepNumber: 1, stepDescription: "", hazards: [] })
              }
            >
              Add First Step
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Sub-component to handle the nested Hazards array
const HazardsList = ({
  control,
  stepIndex,
}: {
  control: Control<TCreateJSA>;
  stepIndex: number;
}) => {
  const {
    fields: hazards,
    append: appendHazard,
    remove: removeHazard,
  } = useFieldArray({
    control,
    name: `steps.${stepIndex}.hazards`,
  });

  return (
    <div className="space-y-4">
      {hazards.map((hazard, hIndex) => (
        <div
          key={hazard.id}
          className="bg-white border border-neutral-200 p-4 rounded-md relative"
        >
          <button
            type="button"
            onClick={() => removeHazard(hIndex)}
            className="absolute top-4 right-4 text-neutral-400 hover:text-red-500 transition-colors"
          >
            <Icon name="X" className="h-4 w-4" />
          </button>

          <div className="space-y-2 max-w-[90%]">
            <label className="text-sm font-medium">Hazard Description</label>
            <Input
              {...control.register(
                `steps.${stepIndex}.hazards.${hIndex}.customHazardDescription` as const,
              )}
              placeholder="Describe the hazard or select from Risk Register"
            />
            {/* TODO: Add Risk Register Dropdown selector */}
          </div>

          <div className="mt-4 pt-4 border-t border-dashed border-neutral-200">
            <h5 className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
              Controls
            </h5>
            <ControlsList
              control={control}
              stepIndex={stepIndex}
              hazardIndex={hIndex}
            />
          </div>
        </div>
      ))}

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() =>
          appendHazard({ customHazardDescription: "", controlsApplied: [] })
        }
        className="flex items-center gap-1 mt-2 text-xs"
      >
        <Icon name="Plus" className="h-3 w-3" />
        Add Hazard
      </Button>
    </div>
  );
};

// Sub-component to handle the nested Controls array
const ControlsList = ({
  control,
  stepIndex,
  hazardIndex,
}: {
  control: Control<TCreateJSA>;
  stepIndex: number;
  hazardIndex: number;
}) => {
  const {
    fields: controls,
    append: appendControl,
    remove: removeControl,
  } = useFieldArray({
    control,
    name: `steps.${stepIndex}.hazards.${hazardIndex}.controlsApplied`,
  });

  return (
    <div className="space-y-2">
      {controls.map((ctrl, cIndex) => (
        <div key={ctrl.id} className="flex items-center gap-2">
          <Input
            {...control.register(
              `steps.${stepIndex}.hazards.${hazardIndex}.controlsApplied.${cIndex}.customControlDescription` as const,
            )}
            placeholder="Describe control measure"
            className="text-sm h-8"
          />
          <button
            type="button"
            onClick={() => removeControl(cIndex)}
            className="text-neutral-400 hover:text-red-500"
          >
            <Icon name="Trash2" className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => appendControl({ customControlDescription: "" })}
        className="text-xs text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 mt-1"
      >
        <Icon name="Plus" className="h-3 w-3" /> Add Control Measure
      </button>
    </div>
  );
};

export default HazardsStep;
